/**
 * Cliente HTTP minimalista para llamadas API del backend.
 *
 * Wrapper delgado sobre fetch() que:
 * - Añade headers JSON por defecto
 * - Lanza Error con el detalle del backend ante respuestas no-2xx
 * - Parsea JSON automáticamente
 *
 * Mantiene la misma semántica que el hook `useDataFetching`,
 * pero como función pura (sin estado React). Ideal para módulos de API.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Lanza un fetch contra el backend.
 * @param {string} endpoint - Ruta relativa, p.ej. "/api/health"
 * @param {object} options  - Opciones de fetch (method, body, headers, ...)
 * @returns {Promise<any>}  - JSON parseado
 * @throws {Error}          - Con mensaje = data.detail || `Error ${status}`
 */
export async function apiFetch(endpoint, options = {}) {
  const url = `${BACKEND_URL}${endpoint}`;
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });

  if (!response.ok) {
    let detail;
    try {
      const errorData = await response.json();
      detail = errorData?.detail;
    } catch (_e) {
      // No body JSON; ignorar
    }
    throw new Error(detail || `Error ${response.status}`);
  }

  return response.json();
}

export const apiGet = (endpoint, params) => {
  const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
  return apiFetch(`${endpoint}${qs}`, { method: 'GET' });
};

export const apiPost = (endpoint, data) =>
  apiFetch(endpoint, { method: 'POST', body: JSON.stringify(data) });

export const apiPut = (endpoint, data) =>
  apiFetch(endpoint, { method: 'PUT', body: JSON.stringify(data) });

export const apiDelete = (endpoint) =>
  apiFetch(endpoint, { method: 'DELETE' });

export default { apiFetch, apiGet, apiPost, apiPut, apiDelete };
